import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { CircularProgress, Link, TextField, WithStyles } from '@material-ui/core';
import { Domain, SearchOutlined } from '@material-ui/icons';
import { createStyles, withStyles } from '@material-ui/styles';
import { Autocomplete, Stack } from '@mui/material';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import _ from 'lodash';
import 'moment/locale/it';
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import ReduxLanguage from '../../components/ReduxLanguage';
import CylindersFilterBar from '../../components/SearchBar/CylindersFilterBar';
import MksCylinderForm from '../../components/Forms/MksCylinderForm';
import { ModalTypes } from '../../components/Modals/ModalTypes';
import * as MksActions from '../../redux/mks/mks.actions';
import * as CreditsActions from '../../redux/credits/credits.actions';
import * as ModalsActions from '../../redux/modals/modals.actions';
import { IState } from '../../redux/store';
import { ICylinderInDTO } from '../../repository/jago/model/input/ICylinderInDTO';
import { IDomainOptionDTO } from '../../repository/jago/model/input/IDomainOptionDTO';
import { IMksImportInDTO } from '../../repository/jago/model/input/IMksImportInDTO';
import { ICylindersRequestParamsDTO, IWalletPlantsRequestParamsDTO, IMksImportsRequestParamsDTO } from '../../repository/jago/model/output/RequestParamsDTOs';
import { getDomainsOptions, getDomainNameFromHostname } from '../../repository/utils';
import translations from '../../translations/i18next';

const styles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
  },
  text: {
    margin: 0,
    marginBottom: 15,
  },
  logo: {
    width: '20%',
  },
  innerHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  header: {
    backgroundColor: 'white',
    width: '100%',
    zIndex: 200,
    padding: 10,
    paddingRight: 20,
    borderBottom: '2px solid #5AC0B1',
  },
  tableContainer: {
    display: 'flex',
    backgroundColor: 'white',
    height: 'calc(100vh - 170px)',
    width: '100%'
  },
});

export interface IMksCylindersPageState {
  domainsOptions: IDomainOptionDTO[];
  rowsPerPage: number;
  isLoading: boolean;
  domainSearchText: string;
}

type IReduxProps = ConnectedProps<typeof connector>;

export interface IMksCylindersPageProps extends WithStyles<typeof styles> {
  language: string;
  cylinders: PaginatedDataDTO<ICylinderInDTO>;
  selectedHostname: string;
  selectedDomain: IDomainOptionDTO | null;
  lastImport: IMksImportInDTO | {};
}

export type ComponentProps = IMksCylindersPageProps & IReduxProps;

class MksCylindersPage extends React.Component<ComponentProps, IMksCylindersPageState> {
  private cylindersColumns: GridColumns<ICylinderInDTO>;
  
  constructor(props: ComponentProps) {
    super(props);
    this.state = {
      domainsOptions: [],
      rowsPerPage: 100,
      isLoading: false,
      domainSearchText: '',
    }
  }

  public async componentDidMount() {
    await this.fetchDomainsOptions('');
    
    const { selectedDomain, dispatch, cylinders } = this.props;
    // Check if data is already cached in state
    const hasCylindersData = cylinders?.content && cylinders.content.length > 0;
    
    try {
      this.setState({ isLoading: true });
      if (selectedDomain) {
        // Only fetch if there's no cached data
        if (!hasCylindersData) {
          await this.fetchCylindersForSelectedDomain();
        } else {
          this.setState({ isLoading: false });
        }
      } else {
        // Check localStorage for saved domain
        const savedHostname = localStorage.getItem('masterKeyDomain');
        if (savedHostname) {
          const params: IWalletPlantsRequestParamsDTO = { 
            hostname: savedHostname,
            page: 0,
            pageSize: 50,
            walletId: undefined
          };
          const domainsResponse = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
          if (domainsResponse && domainsResponse.length > 0) {
            const domainsOptions = getDomainsOptions(domainsResponse);
            if (domainsOptions && domainsOptions.length > 0) {
              const domainOption = domainsOptions[0];
              dispatch<any>(MksActions.setSelectedDomain(domainOption));
              this.setState({ domainSearchText: domainOption.label, domainsOptions }, async () => {
                // Only fetch if there's no cached data
                if (!hasCylindersData) {
                  await this.fetchCylindersForSelectedDomain();
                } else {
                  this.setState({ isLoading: false });
                }
              });
            }
          } else {
            this.setState({ isLoading: false });
          }
        } else {
          this.setState({ isLoading: false });
        }
      }
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  private async fetchCylindersForSelectedDomain() {
    const { selectedDomain, dispatch } = this.props;
    if (!selectedDomain) return;

    const params: ICylindersRequestParamsDTO = {
      hostname: selectedDomain.value.hostname,
      page: 0,
      pageSize: 100,
    };

    dispatch<any>(MksActions.resetMksCylindersData());
    await this.onSearch(params);
    await this.fetchLastImport();
  }

  public async fetchDomainsOptions(searchText: string) {
    const { dispatch } = this.props;
    try {
      const params: IWalletPlantsRequestParamsDTO = { 
        hostname: searchText,
        page: 0,
        pageSize: 50,
        walletId: undefined
      };
      const domainsResponse = await dispatch<any>(CreditsActions.fetchDomainsSearch(params));
      if (domainsResponse) {
        const domainsOptions = getDomainsOptions(domainsResponse);
        this.setState({ domainsOptions });
      }
    } catch (error) {
      console.error('Error fetching domains:', error);
    }
  }

  public async onSearch(params: ICylindersRequestParamsDTO) {
    const { dispatch } = this.props;
    try {
      this.setState({ isLoading: true });
      await dispatch<any>(MksActions.fetchCylinders(params));
      this.setState({ isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
    }
  }

  private async fetchLastImport() {
    const { dispatch, selectedDomain } = this.props;
    if (!selectedDomain) return;

    try {
      const params: IMksImportsRequestParamsDTO = {
        hostname: selectedDomain.value.hostname,
        page: 0,
        pageSize: 1,
      };
      await dispatch<any>(MksActions.fetchLastMksImport(params));
    } catch (error) {
      // Gestisci errore silenziosamente
    }
  }

  public onResetFilters() {
    const { dispatch } = this.props;
    dispatch<any>(MksActions.setSelectedDomain(null));
    localStorage.removeItem('masterKeyDomain');
    this.setState({ domainSearchText: '' });
  }

  public onDomainSelect(domain: IDomainOptionDTO | null) {
    const { dispatch } = this.props;
    dispatch<any>(MksActions.setSelectedDomain(domain));
    
    // Se viene deselezionato il dominio, resetta anche il testo di ricerca centrale e localStorage
    if (!domain) {
      this.setState({ domainSearchText: '' });
      localStorage.removeItem('masterKeyDomain');
    } else {
      // Save domain hostname in localStorage
      localStorage.setItem('masterKeyDomain', domain.value.hostname);
      this.setState({ domainSearchText: domain.label }, () => {
        dispatch<any>(MksActions.resetMksCylindersData());
        dispatch<any>(MksActions.fetchMksDataForSelectedDomain());
      });
    }
  }

  public onSelectDomainFromEmptyState(domain: IDomainOptionDTO | null) {
    const { dispatch } = this.props;
    
    if (domain) {
      dispatch<any>(MksActions.setSelectedDomain(domain));
      // Save domain hostname in localStorage
      localStorage.setItem('masterKeyDomain', domain.value.hostname);
      this.setState({ domainSearchText: domain.label }, () => {
        dispatch<any>(MksActions.resetMksCylindersData());
        dispatch<any>(MksActions.fetchMksDataForSelectedDomain());
      });
    } else {
      localStorage.removeItem('masterKeyDomain');
      this.setState({ domainSearchText: '' });
    }
  }

  public onFetchDomainsFromEmptyState(value: string) {
    this.fetchDomainsOptions(value);
  }

  public async onFetchCylindersPerPage(page: number) {
    const { selectedDomain } = this.props;
    const { rowsPerPage } = this.state;
    
    if (!selectedDomain) return;
    
    const params: ICylindersRequestParamsDTO = { 
      hostname: selectedDomain.value.hostname,
      page, 
      pageSize: rowsPerPage
    };
    
    await this.onSearch(params);
  }

  public handleChangeRowsPerPage(pageSize: number) {
    const { selectedDomain } = this.props;
    
    if (!selectedDomain) return;
    
    this.setState({ rowsPerPage: pageSize }, async () => {
      const params: ICylindersRequestParamsDTO = { 
        hostname: selectedDomain.value.hostname,
        page: 0, 
        pageSize
      };
      
      await this.onSearch(params);
    });
  }

  public onRowClick(cylinderData: ICylinderInDTO) {
    const { dispatch } = this.props;
    dispatch<any>(MksActions.setSelectedCylinder(cylinderData));
    dispatch<any>(ModalsActions.showModal(`MKS_CYLINDER_DETAILS_MODAL`, {
      modalType: ModalTypes.OPERATIONAL_VIEW_MODAL,
      modalProps: {
        content: <MksCylinderForm cylinderData={cylinderData} />,
        onCancel: () => dispatch<any>(ModalsActions.hideModal(`MKS_CYLINDER_DETAILS_MODAL`)),
      }
    }));
  }

  render() {
    const { classes, cylinders, selectedHostname, selectedDomain } = this.props;
    const { rowsPerPage, domainsOptions, domainSearchText } = this.state;
    
    this.cylindersColumns = [
      {
        field: 'id',
        headerName: translations.t('mks.id'),
        type: 'number',
        sortable: true,
        filterable: true,
        minWidth: 80,
        flex: 1,
      },
      {
        field: 'externalId',
        headerName: translations.t('mks.externalId'),
        type: 'number',
        sortable: true,
        filterable: true,
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'doorName',
        headerName: translations.t('mks.doorName'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 180,
        flex: 3,
        renderCell: (params: GridRenderCellParams<any>) => {
          const cylinder = params.row;
          return <h3 style={{ marginLeft: 0, fontWeight: 'bold' }}>{cylinder.doorName}</h3>
        }
      },
      {
        field: 'marking',
        headerName: translations.t('mks.marking'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'type',
        headerName: translations.t('mks.type'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 120,
        flex: 2,
      },
      {
        field: 'insideLength',
        headerName: translations.t('mks.insideLength'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'outsideLength',
        headerName: translations.t('mks.outsideLength'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 100,
        flex: 1,
      },
      {
        field: 'quantity',
        headerName: translations.t('mks.quantity'),
        type: 'number',
        sortable: true,
        filterable: true,
        minWidth: 80,
        flex: 1,
      }
    ];

    return (
      <div className={classes.container}>
        <div className={classes.header} style={{ backgroundColor: 'white' }}>
          <CylindersFilterBar
            onSearch={(params) => this.onSearch(params)}
            onResetFilters={() => this.onResetFilters()}
            onFilterDomains={(value) => this.fetchDomainsOptions(value)}
            onDomainSelect={(domain) => this.onDomainSelect(domain)}
            domainsOptions={domainsOptions}
            selectedHostname={selectedHostname}
            selectedDomain={selectedDomain}
            isVisible={!!selectedHostname}
            lastImport={this.props.lastImport}
          />
        </div>
        <div className={classes.tableContainer}>
          {selectedHostname ? (
            <DataGrid
              getRowId={(d) => d.id}
              rows={cylinders.content || []}
              columns={this.cylindersColumns}
              rowHeight={50}
              loading={this.state.isLoading}
              onPageSizeChange={(pageSize) => this.handleChangeRowsPerPage(pageSize)}
              onPageChange={page => this.onFetchCylindersPerPage(page)}
              onRowClick={(params) => this.onRowClick(params.row)}
              page={(cylinders?.pagination as PaginationDTO)?.number}
              rowCount={(cylinders?.pagination as PaginationDTO)?.totalElements}
              pageSize={rowsPerPage}
              paginationMode="server"
              componentsProps={{
                pagination: {
                  labelRowsPerPage: translations.t('forms.rowsPerPage')
                }
              }}
              components={{
                NoRowsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                      <SearchOutlined style={{ alignSelf: 'center' }} />
                      <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                        <ReduxLanguage languageKey='mks.noCylindersFoundTitle' />
                      </h3>
                      <h4 style={{ textAlign: 'center', marginTop: 0 }}>
                        <ReduxLanguage languageKey='mks.noCylindersFoundDescription' />
                      </h4>
                    </div>
                  </Stack>
                ),
                NoResultsOverlay: () => (
                  <Stack height="100%" alignItems="center" justifyContent="center">
                    <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                      <ReduxLanguage languageKey='mks.noCylindersFoundTitle' />
                    </h3>
                    <h4 style={{ textAlign: 'center', marginTop: 0 }}>
                      <ReduxLanguage languageKey='mks.noCylindersFoundDescription' />
                    </h4>
                  </Stack>
                )
              }}
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
              }}
            />
          ) : (
            <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
              {this.state.isLoading ? (
                <CircularProgress style={{ marginBottom: 24 }} />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center'}}>
                    <Domain style={{ fontSize: 40, marginBottom: 10 }} />
                    <h2 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                      <ReduxLanguage languageKey='mks.hostnameRequiredTitle' />
                    </h2>
                    <h3 style={{ textAlign: 'center', marginTop: 10, marginBottom: '24px' }}>
                      <ReduxLanguage languageKey='mks.hostnameRequiredDescription' />
                    </h3>
                    <Autocomplete
                      options={domainsOptions}
                      value={selectedDomain || null}
                      inputValue={selectedDomain ? selectedDomain.label : domainSearchText}
                      getOptionLabel={(option) => {
                        if (!option) return '';
                        if (option.label) return option.label;
                        if (option.value?.name) return option.value.name;
                        if (option.value?.hostname) return getDomainNameFromHostname(option.value.hostname);
                        return '';
                      }}
                      isOptionEqualToValue={(option, value) => option?.value?.hostname === value?.value?.hostname}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          variant="outlined" 
                          label={<ReduxLanguage languageKey="mks.hostnameRequired" />}
                          required
                          error={!selectedDomain}
                          helperText={''}
                          style={{ minWidth: 300 }}
                        />
                      )}
                      onInputChange={(event, newInputValue) => { 
                        this.setState({ domainSearchText: newInputValue }); 
                        const debouncedDomainSearch = _.debounce((text) => this.onFetchDomainsFromEmptyState(text), 500);
                        debouncedDomainSearch(newInputValue); 
                      }}
                      onChange={(event, newValue) => this.onSelectDomainFromEmptyState(newValue)}
                      noOptionsText="No domains found"
                      loadingText="Loading domains..."
                      clearOnBlur={false}
                      clearOnEscape={true}
                    />
                    <h3 style={{ marginTop: 20 }}>
                      <Link href="https://help-center.sofialocks.com/vasho-master-key-systems-management/" target="_blank" style={{ textDecoration: 'underline' }}>
                        {translations.t('forms.findOutMoreAboutThisFeature')}
                      </Link>
                    </h3>
                  </div>
                </div>
              )}
            </Stack>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state: IState) {
  return {
    language: state.settings.language,
    cylinders: state.mks.cylinders.data,
    selectedHostname: state.mks.selectedHostname,
    selectedDomain: state.mks.selectedDomain,
    lastImport: state.mks.imports.lastImport,
  };
}

const connector = connect(mapStateToProps);

export default connector(withStyles(styles)(MksCylindersPage as any));
